import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import moment from "moment-timezone";
import { connect } from "react-redux";
import { resetOrgData } from "redux/actions/Page";
import SimpleTable from "views/Components/SimpleTable";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import { RECEIVE_SETTINGS } from "redux/actions/Session";
// INTERNAL COMPONENTS
import Loading from "../../Components/Loading";
import MSPDetailDialog from "../../Components/MSP/MSPDetailDialog";
import { Org } from "lib/models/Org";
import { Settings } from "lib/models/Settings";
import Request from "lib/api/Request";
import { loadPageData } from "redux/actions/Request";

import { renderConfigCell } from "../../Components/MSP/helpers";

import {
  getChildOrgs,
  getOrgRoles,
  getShouldDisplayDataRetentionMessage,
} from "./utils";

const renderDateCell = (column, model) => {
  const timeValue = model[column.field];
  if (!timeValue) {
    return "Pending";
  }
  return moment.utc(timeValue).tz(moment.tz.guess(true)).format("lll z");
};

const MSPAccountsPageView = (props) => {
  const { users, orgId, reload, routeToOrg, childOrganizationsList } = props;
  const [persons, setPersons] = useState([]);
  const [loading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [totalAgents, setTotalAgents] = useState({});
  const [openFindings, setOpenFindings] = useState({});
  const [enabledRules, setEnabledRules] = useState({});
  const [adminUsersIds, setAdminUsersIds] = useState([]);
  const [linkForNewTab, setLinkForNewTab] = useState({});
  const [isConfirming, setIsConfirming] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [isolatedAgents, setIsolatedAgents] = useState({});
  const [selectedOrgModel, setSelectedOrgModel] = useState(false);
  const [isAccountDialogOpen, setIsAccountDialogOpen] = useState(false);
  const [userIdsForAccountCreation, setUserIdsForAccountCreation] = useState(
    []
  );
  const [
    shouldDisplayDataRetentionMessage,
    setShouldDisplayDataRetentionMessage,
  ] = useState(false);

  const columns = [
    {
      title: "Account Name",
      field: "name",
      searchable: true,
    },
    {
      title: "User Count",
      field: "config.userCount",
      renderValue: renderConfigCell(null, 0),
    },
    {
      title: "Edition",
      field: "config.license",
      searchable: true,
      renderValue: renderConfigCell(null, ""),
    },
    {
      title: "Rules",
      field: "enabledRules",
      searchable: true,
      renderValue: renderConfigCell(enabledRules, 0),
    },
    {
      title: "Unresolved findings",
      field: "openFindings",
      searchable: true,
      renderValue: renderConfigCell(openFindings, 0),
    },
    {
      title: "Account created",
      field: "created",
      searchable: true,
      renderValue: renderDateCell,
    },
  ];

  const blumiraAgentColumns = [
    {
      title: "Agent Count",
      field: "totalAgents",
      searchable: true,
      renderValue: renderConfigCell(totalAgents, 0),
    },
    {
      title: "Agents Isolated",
      field: "isolatedAgents",
      searchable: true,
      renderValue: renderConfigCell(isolatedAgents, 0),
    },
  ];

  useEffect(() => {
    if (childOrganizationsList.length) {
      const outputFields = ["status", "name", "orgId", "id"];
      const childOrgsIdList = childOrganizationsList
        .map((obj) => obj.id)
        .join();

      const openFindingsRequest = new Request(
        "/finding",
        [
          { field: "orgId", operator: "in", value: childOrgsIdList },
          { field: "status", operator: "eq", value: 0 },
        ],
        { outputFields }
      );

      const rulesRequest = new Request(
        "/rule",
        [
          { field: "orgId", operator: "in", value: childOrgsIdList },
          { field: "status", operator: "eq", value: 1 },
        ],
        { outputFields }
      );

      const totalAgentsRequest = new Request(
        "/lcagent",
        [{ field: "orgId", operator: "in", value: childOrgsIdList }],
        { outputFields: ["id", "orgId"] }
      );

      const isolatedAgentsRequest = new Request(
        "/lcagent",
        [
          { field: "orgId", operator: "in", value: childOrgsIdList },
          { field: "isIsolated", operator: "eq", value: true },
        ],
        { outputFields: ["id", "orgId"] }
      );

      // requests that should fire regardless of features
      const promises = [openFindingsRequest.get(), rulesRequest.get()];

      // if the feature is enabled, include license restricted
      // requests else only fire off the defaults
      let promisesToExecute = props.isBlumiraAgentEnabled
        ? promises.concat([
            totalAgentsRequest.get(),
            isolatedAgentsRequest.get(),
          ])
        : promises;

      Promise.all(promisesToExecute).then((objList) => {
        const [
          openFindingsResponse,
          rulesResponse,
          totalAgentsResponse,
          isolatedAgentsResponse,
        ] = objList;

        const findingsByOrg = _(openFindingsResponse)
          .countBy((obj) => obj.orgId)
          .value();
        setOpenFindings(findingsByOrg);

        const rulesByOrg = _(rulesResponse)
          .countBy((obj) => obj.orgId)
          .value();
        setEnabledRules(rulesByOrg);

        if (totalAgentsResponse) {
          const totalAgentsByOrg = _(totalAgentsResponse)
            .countBy((obj) => obj.orgId)
            .value();
          setTotalAgents(totalAgentsByOrg);
        }

        if (isolatedAgentsResponse) {
          const isolatedAgentsByOrg = _(isolatedAgentsResponse)
            .countBy((obj) => obj.orgId)
            .value();
          setIsolatedAgents(isolatedAgentsByOrg);
        }

        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, [childOrganizationsList]);

  useEffect(() => {
    if (users && orgId && !!selectedOrgModel?.id) {
      let usersEnrolledInOrg = users
        .filter((user) =>
          user.orgRoles.find((role) => role.orgId === selectedOrgModel.id)
        )
        .map((user) => user.id);
      setUserIdsForAccountCreation(usersEnrolledInOrg);
    } else {
      let usersEnrolledInOrg = users
        .filter((user) => user.orgRoles.find((role) => role.roleId === 10))
        .map((user) => user.id);
      setUserIdsForAccountCreation(usersEnrolledInOrg);
    }
    const adminUsers = users
      .filter((user) => user.orgRoles.find((role) => role.roleId === 10))
      .map((user) => user.id);
    setAdminUsersIds(adminUsers);
  }, [users, orgId, selectedOrgModel]);

  useEffect(() => {
    const persons = userIdsForAccountCreation.map((userId) => {
      const fullUser = users.find(({ id }) => userId === id);

      const orgRoles = getOrgRoles(fullUser);

      return {
        id: userId,
        orgRoles: orgRoles,
      };
    });

    setPersons(persons);
  }, [userIdsForAccountCreation, users]);

  const handleEditTableRowClick = (rowDetails) => {
    const { model } = rowDetails;

    // if the 'model' in row details has a hash value we have everything
    // we need to populate the detail dialog, otherwise, 'model' in the
    // rowDetails is just JSON so set a new model for editing and saving
    let orgModel = model && model.hash ? model : new Org(model);

    setSelectedOrgModel(orgModel);
    toggleAccountDialog();
  };

  const handleOpenApp = (rowDetails) => {
    // model here is the selected org
    const { model = {} } = rowDetails;

    routeToOrg({
      type: "PAGE",
      payload: {
        orgId: model.id,
        toplevel: "dashboard",
        secondlevel: "summary",
      },
    });
  };

  const handleOpenInNewTab = (rowDetails) => {
    const { model = {} } = rowDetails;
    setLinkForNewTab({
      type: "PAGE",
      payload: {
        orgId: model.id,
        toplevel: "dashboard",
        secondlevel: "summary",
      },
    });
  };

  const toggleAccountDialog = () => {
    setIsAccountDialogOpen(!isAccountDialogOpen);
    setIsConfirming(false);
    setShouldDisplayDataRetentionMessage(false);
  };

  const handleAddNewAccount = (event) => {
    // instantiate a new model tying this child to
    // its parent and default the market to MSP
    setSelectedOrgModel(
      new Org({ parentId: orgId, market: 20, config: { license: "FREE" } })
    );
    toggleAccountDialog();
  };

  const getRolesForSaving = (orgRoles) => {
    let rolesToReturn = orgRoles;
    let isUpdating = !!selectedOrgModel.id;
    const alreadyEnrolled = orgRoles.find(
      ({ orgId }) => orgId === selectedOrgModel.id
    );

    // if updating users on the org that
    // are not enrolled in selected org
    if (isUpdating && !alreadyEnrolled) {
      // default to 'responder' only
      let newOrgRole = {
        roleId: 40,
        orgId: selectedOrgModel.id,
      };

      // add new role to user's org roles
      // inside of org's persons array
      rolesToReturn = rolesToReturn.concat([newOrgRole]);
    }

    return rolesToReturn;
  };

  const handleSubmit = (model) => {
    let isUpdating = !!selectedOrgModel.id;

    setIsSaving(true);

    // set up 'persons' value
    // these are the user ids selected in the simple table
    const persons = userIdsForAccountCreation.map((userId) => {
      // get list of user's org roles
      const { orgRoles } = users.find(({ id }) => userId === id);
      return {
        id: userId,
        orgRoles: getRolesForSaving(orgRoles),
      };
    });

    selectedOrgModel.set({ persons: persons });

    // if no license set, default to ADVANCED
    if (!selectedOrgModel.config?.license) {
      selectedOrgModel.set({
        config: {
          ...selectedOrgModel.config,
          license: "ADVANCED",
        },
      });
    }

    const func = isUpdating ? "update" : "create";
    selectedOrgModel[func]()
      .then((savedOrg) => {
        reload();
        toggleAccountDialog();
        setSelectedOrgModel(false);
        setIsConfirming(false);
        setIsSaving(false);
      })
      .catch((err) => {
        setErrorMessage(err.toString());
        setIsConfirming(false);
        setIsSaving(false);
      });
  };

  // for the second step of creating an account
  // user selects users to be enrolled
  const handleSelectUser = (selectedUserIds) => {
    setUserIdsForAccountCreation(selectedUserIds);
  };

  const handleChange = (value, name) => {
    let names = name.split(".");

    if (names.find((name) => name === "license")) {
      // this is where we can conditionally show a warning
      const newShouldShowMessageBoolean = getShouldDisplayDataRetentionMessage(
        value,
        selectedOrgModel
      );
      setShouldDisplayDataRetentionMessage(newShouldShowMessageBoolean);
    }

    // this is to handle nested props
    // i.e. config.license on org model
    if (names.length > 1)
      selectedOrgModel.set({
        [names[0]]: { ...selectedOrgModel[names[0]], [names[1]]: value },
      });
    else selectedOrgModel.set({ [name]: value });
  };

  return (
    <>
      {loading && <Loading />}
      <SimpleTable
        isNorthStar
        isFetching={loading}
        title="Organizations"
        data={childOrganizationsList}
        columns={
          props.isBlumiraAgentEnabled
            ? columns.concat(blumiraAgentColumns)
            : columns
        }
        contextMenuItems={[
          {
            onClick: handleEditTableRowClick,
            text: "Account details",
            datacy: "",
          },
          {
            onClick: handleEditTableRowClick,
            text: "Change user count",
            datacy: "",
          },
          {
            onClick: handleOpenApp,
            text: "Open in Blumira App",
            datacy: "",
          },
          {
            onClick: handleOpenInNewTab,
            link: linkForNewTab,
            text: "Open in New Tab",
          },
        ]}
        toolbarActions={[
          {
            icon: AddCircleIcon,
            tooltip: "Add New Account",
            onClick: handleAddNewAccount,
          },
        ]}
      />
      <MSPDetailDialog
        tableData={users}
        context={"account"}
        isSaving={isSaving}
        model={selectedOrgModel}
        open={isAccountDialogOpen}
        isConfirming={isConfirming}
        errorMessage={errorMessage}
        handleChange={handleChange}
        toggle={toggleAccountDialog}
        handleSubmit={handleSubmit}
        handleSelectTableItem={handleSelectUser}
        selectedTableData={userIdsForAccountCreation}
        disabledTableData={adminUsersIds}
        personsList={persons}
        reloadData={reload}
        shouldDisplayDataRetentionMessage={shouldDisplayDataRetentionMessage}
      />
    </>
  );
};

MSPAccountsPageView.propTypes = {
  loading: PropTypes.bool,
  orgId: PropTypes.string.isRequired,
  childOrganizationsList: PropTypes.arrayOf(PropTypes.shape({})),
};

MSPAccountsPageView.defaultProps = {
  users: [],
  loading: false,
};

const mapStateToProps = (state) => {
  const { session, location } = state;
  const { orgId: mspOrgId } = location.payload;

  const orgListToFilter = _.get(session, `settings.userOrgs`, []);

  // getChildOrgs expects a a list of orgs and an
  // org id to filter and returns an array of orgs
  const childOrgsList = getChildOrgs(orgListToFilter, mspOrgId);

  /* 
    TODO: use the following logic to request 
    lc API endpoints when building table data
  */
  const licenseFeatureArray = _.get(
    state.license,
    ["licenseMap", `${mspOrgId}`, "license_features"],
    []
  );
  const isBlumiraAgentEnabled = licenseFeatureArray.find(
    (feature) => feature === "LOGSHIPPING"
  );

  return {
    orgId: mspOrgId,
    license: state.license,
    childOrganizationsList: childOrgsList,
    isBlumiraAgentEnabled: !!isBlumiraAgentEnabled,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    reload: () => {
      // refresh table data
      dispatch(loadPageData(true));

      // refresh settings for license updates
      const settings = new Settings();
      settings.read().then(() => {
        dispatch({ type: RECEIVE_SETTINGS, settings });
      });
    },
    routeToOrg: (props) => {
      dispatch(resetOrgData);
      dispatch({ type: "PAGE", payload: props.payload });
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MSPAccountsPageView);
